import React, { useId } from 'react';

import { Flex } from 'common/components/Flex';
import ImageUpload from 'common/components/ImageUpload';
import StateMessages from 'common/components/Input/StatefulInput/StateMessages';
import type { CommonTypographyProps } from 'common/components/Typography';
import { BodyRegularBR1, HeadlineH8 } from 'common/components/Typography';
import { FieldChangeTrigger } from 'common/enums/form';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { FormGroupData } from 'common/types/field';
import type { FieldChangeTriggerHandler } from 'common/types/form';
import { useImages } from '../../hooks/images';

export interface ImagesProps<FieldName extends string = string> {
  name: FieldName;
  label: string;
  value?: string;
  errors?: string[];
  hasError?: boolean;
  required?: boolean;
  onChange?: FieldChangeTriggerHandler<FieldName>;
}

export type ImagesFormData = FormGroupData<ImagesProps>;

const ACCEPTED_FILE_TYPES = 'image/gif, image/jpeg, image/png, application/pdf';

const textStyles: CommonTypographyProps = {
  pt: ['sp_4', 'sp_0'],
  mb: ['sp_8', null, null, 'sp_12'],
};

const Images = (props: ImagesProps) => {
  const { t } = useLocalization();
  const { images, deleteImage, uploadImage } = useImages(props);

  const id = useId();
  const descriptionId = `${id}-description`;
  const stateMessagesId = `${id}-state-messages`;

  const handleBlur = () => {
    props.onChange?.({
      fieldName: props.name,
      value: '',
      trigger: FieldChangeTrigger.Blur,
    });
  };

  return (
    <Flex flexDirection="column" width="100%">
      <HeadlineH8 {...textStyles}>
        {t('h24_contact_form_add_file')}
        {props.required ? '' : ` (${t('h24_optional')})`}
      </HeadlineH8>
      <BodyRegularBR1 id={descriptionId} {...textStyles}>
        {t('h24_contact_form_add_file_description')}
      </BodyRegularBR1>

      <ImageUpload
        images={images}
        onInputBlur={handleBlur}
        deleteImage={deleteImage}
        uploadImages={uploadImage}
        accept={ACCEPTED_FILE_TYPES}
        ariaDescribedBy={
          props.errors?.length && props.errors.length > 0
            ? `${descriptionId} ${stateMessagesId}`
            : descriptionId
        }
      >
        <StateMessages id={stateMessagesId} errors={props.errors} hasError={props.hasError} />
      </ImageUpload>
    </Flex>
  );
};

export default Images;
